/**
 * Phase 15c (D-164, D-165): the native app. It signs in for a session token
 * (sent as `Authorization: Bearer`, never as a cookie), names itself and its
 * version in a header on every request, and names the product it is in.
 */

/** The API's own version (docs/api/README.md): additions keep the major; a removal or a change of meaning raises it. */
export const OPPY_API_VERSION = '1.0.0';

/** The app's URL scheme, its bundle id: where a connector sign-in returns to (ASWebAuthenticationSession). */
export const APP_URL_SCHEME = 'tech.dbrain.oppy';

/** `ios/0.1.0 (261030.0915)` */
export const CLIENT_HEADER = 'x-oppy-client';
/** `companygpt` or `companycode`; the app has no Origin for the license's product check */
export const PRODUCT_HEADER = 'x-oppy-product';

export const APP_PLATFORMS = ['ios', 'android'] as const;
export type AppPlatform = (typeof APP_PLATFORMS)[number];
/** How a session is used: a browser (the cookie) or the app (the bearer token) */
export type SessionClient = 'browser' | AppPlatform;

export interface AppClientInfo {
  platform: AppPlatform;
  version: string;
  build: string | null;
}

const CLIENT_PATTERN = /^(ios|android)\/(\d{1,4}\.\d{1,4}\.\d{1,4})(?: \(([0-9A-Za-z.]{1,20})\))?$/;

export function parseAppClient(value: string | undefined): AppClientInfo | null {
  const match = CLIENT_PATTERN.exec((value ?? '').trim());
  if (!match) return null;
  return { platform: match[1] as AppPlatform, version: match[2] as string, build: match[3] ?? null };
}

export function formatAppClient(info: AppClientInfo): string {
  return `${info.platform}/${info.version}${info.build ? ` (${info.build})` : ''}`;
}

/** Semantic versions, major.minor.patch only: negative when a < b. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export const isVersion = (value: string): boolean => /^\d{1,4}\.\d{1,4}\.\d{1,4}$/.test(value);
